import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { InputFieldComponent } from '@elan/core/ui/form-elements/input-field.component';
import { CheckboxComponent } from '@elan/core/ui/form-elements/checkbox.component';
import { ButtonComponent } from '@elan/core/ui/form-elements/button.component';
import { SnackbarService } from '@elan/services/snackbar.service';
import {
  ReactiveFormsModule,
  FormsModule,
  FormBuilder,
  FormControlName,
  FormGroup,
  Validators,
  FormControl,
} from '@angular/forms';
import { AuthService } from '@elan/services/auth-service';
import { Subscription } from 'rxjs';
import { getErrorMessage } from '@elan/services/errorMessage';
import { LoaderComponent } from '@elan/components/loader-component/loader-component';
import { EncryptedStorageService } from '@elan/services/encrypted-storage.service';
import { Router, ActivatedRoute } from '@angular/router';
@Component({
  selector: 'app-login',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    InputFieldComponent,
    CheckboxComponent,
    ButtonComponent,
    LoaderComponent,
  ],
  templateUrl: './login.html',
  styleUrl: './login.scss',
})
export class Login implements OnInit, OnDestroy {
  loginForm!: FormGroup;
  subscription: Subscription = new Subscription();
  isChecked: boolean = false;
  submit = false;
  constructor(
    private snackbarService: SnackbarService,
    private fb: FormBuilder,
    private authService: AuthService,
    private encryptedStorageService: EncryptedStorageService,
    private router: Router,
    private route: ActivatedRoute
  ) { }
  ngOnInit(): void {
    this.createLoginForm();
    let rememberMe = this.encryptedStorageService.getItem('rememberMe');
    if (rememberMe) {
      this.loginForm.patchValue(rememberMe);
      this.isChecked = true;
    }
    this.loginForm.get('rememberMe')?.valueChanges.subscribe((value) => {
      if (!value) {
        this.encryptedStorageService.removeItem('rememberMe');
      }
    });
  }

  /**
   * Creates a form group for the login form with email, password and rememberMe fields.
   * Email and password fields are required and must satisfy the email and minimum length of 6 characters validators respectively.
   * RememberMe field is optional and defaults to false.
   */
  createLoginForm() {
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required]],
      rememberMe: [false],
    });
  }

  /**
   * Retrieves a FormControl from the login form by its name.
   * Throws an error if the control does not exist in the form.
   * @param controlName The name of the control to retrieve.
   * @returns The FormControl with the given name.
   * @throws Error If the control does not exist in the form.
   */
  getControl(controlName: string): FormControl {
    const control = this.loginForm.get(controlName);
    if (!control) {
      throw new Error(`FormControl '${controlName}' does not exist in the form`);
    }
    return control as FormControl;
  }

  // onSubmit() {
  //   if (!this.loginForm.valid) {
  //     this.loginForm.markAllAsTouched();
  //     return;
  //   }
  //   this.submit = true;
  //   this.subscription.add(this.authService.login(['pageLoader','loginBtnLoader'],this.loginForm.getRawValue()).subscribe({
  //     next: (res:any) => {
  //       this.submit = false;
  //       this.snackbarService.success('Login successful');
  //         this.encryptedStorageService.setItem('token', res);
  //       if (this.loginForm.value.rememberMe) {
  //         this.encryptedStorageService.setItem('rememberMe', this.loginForm.value);
  //       } else {
  //         this.encryptedStorageService.removeItem('rememberMe');
  //       }
  //       this.loginForm.reset();
  //       setTimeout(() => {
  //               this.router.navigate(['/user/dashboard']);
  //       })
  //     },
  //     error: (error) => {
  //       this.snackbarService.error(getErrorMessage(error));
  //       this.submit = false;
  //     }
  //   }))
  // }

  onSubmit() {
    this.submit = true;
    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      return;
    }

    this.subscription.add(
      this.authService
        .login(['pageLoader', 'loginBtnLoader'], this.loginForm.getRawValue())
        .subscribe({
          next: (res: any) => {
            this.submit = false;
            this.snackbarService.success('Login successful');

            this.encryptedStorageService.setItem('token', res);

            if (this.loginForm.value.rememberMe) {
              this.encryptedStorageService.setItem('rememberMe', this.loginForm.value);
            } else {
              this.encryptedStorageService.removeItem('rememberMe');
            }

            this.loginForm.reset();

            const roleId = res?.data?.user?.user_roles?.length
              ? res.data.user.user_roles[0].role_id
              : null;

            setTimeout(() => {
              let returnUrl = this.route?.snapshot?.queryParams?.['return_url'] ?? null;
              if (returnUrl) {
                // Check if returnUrl contains a nested return_url query param
                if (typeof returnUrl === 'string' && returnUrl.includes('return_url=')) {
                  try {
                    // Create a dummy URL to parse the query string
                    const url = new URL('http://dummy.com' + returnUrl);
                    const nestedUrl = url.searchParams.get('return_url');
                    if (nestedUrl) {
                      returnUrl = nestedUrl;
                    }
                    else {
                      returnUrl = '/user/dashboard';
                    }

                  } catch (e) {
                    returnUrl = '/user/dashboard';
                  }
                }
              }

              if (returnUrl && !returnUrl.startsWith('/login')) {
                this.router.navigateByUrl(returnUrl);
              } else {
                this.router.navigate(['/user/dashboard']);
              }
            });
          },

          error: (error) => {
            this.snackbarService.error(getErrorMessage(error));
            this.submit = false;
          },
        })
    );
  }
  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
